import { alpha } from '@mui/material/styles';
import { JLR_PLANT } from '../../constants';

export default function Table(theme) {
  const isJlr = theme.plantName === JLR_PLANT;
  const isDarkMode = theme.palette.mode === 'dark';

  const jlrTableStyles = {
    root: {
      fontFamily: "'Inter', sans-serif",
      background: isDarkMode 
        ? 'linear-gradient(135deg, #1a1d20, #2d3136)'
        : 'linear-gradient(135deg, #ffffff, #fafbfc)',
      borderRadius: '16px',
      overflow: 'hidden',
      border: `1px solid ${isDarkMode 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(45, 74, 45, 0.1)'}`,
      boxShadow: isDarkMode 
        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
        : '0 8px 32px rgba(45, 74, 45, 0.08)',
    },
    
    head: {
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(0, 102, 204, 0.1), rgba(45, 74, 45, 0.05))'
        : 'linear-gradient(135deg, rgba(45, 74, 45, 0.08), rgba(200, 185, 156, 0.05))',
      borderBottom: `2px solid ${isDarkMode 
        ? 'rgba(0, 102, 204, 0.2)' 
        : 'rgba(45, 74, 45, 0.2)'}`,
    },
    
    headerCell: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: '0.875rem',
      color: isDarkMode ? '#FFFFFF' : '#2d4a2d',
      padding: '16px 20px',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      borderRight: `1px solid ${isDarkMode 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(45, 74, 45, 0.05)'}`,
      
      '&:last-child': {
        borderRight: 'none',
      },
      
      '&.MuiTableCell-sortActive': {
        color: isDarkMode ? '#0066cc' : '#2d4a2d',
      },
    },
    
    bodyRow: {
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      borderBottom: `1px solid ${isDarkMode 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(45, 74, 45, 0.05)'}`,
      
      '&:hover': {
        background: isDarkMode 
          ? 'rgba(0, 102, 204, 0.05)'
          : 'rgba(45, 74, 45, 0.03)',
        transform: 'translateY(-1px)',
        boxShadow: isDarkMode 
          ? '0 4px 12px rgba(0, 0, 0, 0.2)'
          : '0 4px 12px rgba(45, 74, 45, 0.08)',
      },
      
      '&.Mui-selected': {
        background: isDarkMode 
          ? 'rgba(0, 102, 204, 0.1)'
          : 'rgba(45, 74, 45, 0.08)',
        
        '&:hover': {
          background: isDarkMode 
            ? 'rgba(0, 102, 204, 0.15)'
            : 'rgba(45, 74, 45, 0.12)',
        },
      },
      
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    
    bodyCell: {
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.875rem',
      color: isDarkMode ? '#FFFFFF' : 'rgba(45, 74, 45, 0.9)',
      padding: '16px 20px',
      borderRight: `1px solid ${isDarkMode 
        ? 'rgba(255, 255, 255, 0.03)' 
        : 'rgba(45, 74, 45, 0.03)'}`,
      
      '&:last-child': {
        borderRight: 'none',
      },
    },
    
    footer: {
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(26, 29, 32, 0.8), rgba(45, 49, 54, 0.6))'
        : 'linear-gradient(135deg, rgba(250, 251, 252, 0.8), rgba(248, 249, 250, 0.6))',
      borderTop: `1px solid ${isDarkMode 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(45, 74, 45, 0.1)'}`,
    },
  };

  const standardTableStyles = {
    root: {
      background: theme.palette.background.paper,
      borderRadius: '8px',
    },
    head: {
      background: alpha(theme.palette.primary.main, 0.04),
    },
    headerCell: {
      fontWeight: 600,
    },
    bodyRow: {
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.04),
      },
    },
    bodyCell: {},
    footer: {
      background: alpha(theme.palette.grey[500], 0.04),
    },
  };

  const styles = isJlr ? jlrTableStyles : standardTableStyles;

  return {
    MuiTable: {
      styleOverrides: {
        root: styles.root,
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: isJlr ? '16px' : '8px',
          background: styles.root.background,
          border: styles.root.border,
          boxShadow: styles.root.boxShadow,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: styles.head,
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: styles.bodyRow,
        head: {
          background: 'transparent',
          '&:hover': {
            background: 'transparent',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
        head: styles.headerCell,
        body: styles.bodyCell,
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: {
          ...styles.footer,
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          color: isJlr ? (isDarkMode ? '#FFFFFF' : '#2d4a2d') : theme.palette.text.primary,
          padding: isJlr ? '16px 24px' : '12px 16px',
        },
        toolbar: {
          minHeight: isJlr ? '64px' : '52px',
        },
        selectLabel: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          color: isJlr ? (isDarkMode ? '#FFFFFF' : 'rgba(45, 74, 45, 0.8)') : theme.palette.text.secondary,
        },
        displayedRows: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          color: isJlr ? (isDarkMode ? '#FFFFFF' : 'rgba(45, 74, 45, 0.8)') : theme.palette.text.secondary,
        },
        actions: {
          '& .MuiIconButton-root': {
            color: isJlr ? (isDarkMode ? '#ffffff' : '#2d4a2d') : theme.palette.text.primary,
            
            '&:hover': {
              background: isJlr 
                ? (isDarkMode 
                  ? 'rgba(0, 102, 204, 0.1)' 
                  : 'rgba(45, 74, 45, 0.08)')
                : alpha(theme.palette.primary.main, 0.04),
            },
            
            '&.Mui-disabled': {
              color: isJlr ? (isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(45, 74, 45, 0.3)') : theme.palette.text.disabled,
            },
          },
        },
      },
    },

    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
          color: 'inherit',
          
          '&:hover': {
            color: isJlr ? (isDarkMode ? '#0066cc' : '#2d4a2d') : theme.palette.primary.main,
          },
          
          '&.Mui-active': {
            color: isJlr ? (isDarkMode ? '#0066cc' : '#2d4a2d') : theme.palette.primary.main,
            
            '& .MuiTableSortLabel-icon': {
              color: 'inherit',
            },
          },
        },
        icon: {
          opacity: 0.7,
          transition: 'opacity 0.2s',
          
          '&:hover': {
            opacity: 1,
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: isJlr ? (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(45, 74, 45, 0.7)') : theme.palette.text.secondary,
          
          '&.Mui-checked': {
            color: isJlr ? (isDarkMode ? '#0066cc' : '#2d4a2d') : theme.palette.primary.main,
          },
          
          '&:hover': {
            background: isJlr 
              ? (isDarkMode 
                ? 'rgba(0, 102, 204, 0.05)' 
                : 'rgba(45, 74, 45, 0.05)')
              : alpha(theme.palette.primary.main, 0.04),
          },
        },
      },
    },
  };
}
